import { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { createContext } from '@chakra-ui/react-utils'
import { useApolloClient } from '@apollo/client'

import { useOAuth2, useAuthQuery } from '../hooks'

const [AuthContextProvider, useAuthContext] = createContext({
  name: 'AuthContext',
  errorMessage:
    'useAuthContext: `context` is undefined. Seems you forgot to wrap the components in `<AuthProvider />`'
})

export { AuthContextProvider, useAuthContext }

const hierarchy = {
  ROLE_ADMIN: ['ROLE_USER', 'ROLE_GUEST'],
  ROLE_USER: ['ROLE_GUEST']
}

const expandAuthorities = (authorities = []) =>
  [].concat(...authorities.map(authority => [authority, ...(hierarchy[authority] || [])]))

export const useAuthProvider = () => {
  const navigate = useNavigate()
  const client = useApolloClient()
  const { token, getToken, clearToken } = useOAuth2({
    scope: process.env.REACT_APP_AUTH_SCOPE,
    clientId: process.env.REACT_APP_AUTH_CLIENT_ID,
    authorizeUrl: process.env.REACT_APP_AUTH_AUTHORIZE_URL,
    redirectUri: `${document.location.protocol}//${document.location.host}/callback`
  })

  const { data, error, loading } = useAuthQuery({ skip: !token })

  const auth = useMemo(
    () =>
      data
        ? {
            ...data,
            authorities: expandAuthorities(data.authorities)
          }
        : undefined,
    [data]
  )

  const oauth = useCallback(() => getToken(), [getToken])
  // const login = useCallback(() => {})
  const logout = useCallback(async () => {
    clearToken()
    await client.clearStore()
    await client.cache.gc()
    navigate('/logout')
  }, [client, navigate, clearToken])

  return {
    auth,
    error,
    loading,
    oauth,
    // login,
    logout
  }
}

export const useAuth = () => {
  const ops = useAuthContext()
  return ops
}
